import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { BorrowerIndexItem } from 'app/borrower-index/services/borrower-index/borrower-index-item';
import { BorrowerIndexService } from 'app/borrower-index/services/borrower-index/borrower-index.service';
import { FilterCriteria } from 'app/models/dto/filter-criteria.model';
import { FilterParameter } from 'app/models/dto/filter-parameter.model';
import { BorrowerIndexGet, BorrowerIndexApplySort, BorrowerIndexNextPage, BorrowerIndexApplyFilter, BorrowerIndexUpdateLoadingState, ClearBorrowerIndexStore } from './borrower-index.actions';
import {catchError} from 'rxjs/operators';
import {of} from 'rxjs';

interface SortStatus {
  [key: string]: 'ASC' | 'DESC';
}


export class BorrowerIndexStateModel {
  borrowerIndexItems: BorrowerIndexItem[];
  loading: boolean;
  nextPageLoading: boolean;
  allFilteredBorrowerIdsLoading: boolean;
  sort: SortStatus;
  page: number;
  nextPageExists: boolean;
  criteria: FilterCriteria;
  relationCriteria: {};
  noDataFound: boolean;
  filterParameters: FilterParameter[];
}

@State<BorrowerIndexStateModel>({
	name: 'borrowerIndex',
	defaults: {
	  borrowerIndexItems: [],
    loading: false,
    nextPageLoading: false,
    allFilteredBorrowerIdsLoading: false,
    sort: { 'borrowers.lastActivityId': 'DESC' },
    page: 1,
    nextPageExists: false,
    criteria: { 'borrowers.status': { 'notIn': ['dead', 'doNotContact'] } },
    relationCriteria: {},
    noDataFound: false,
    filterParameters: [],
  }
})

@Injectable()
export class BorrowerIndexState {

  @Selector()
  static borrowerIndexItems(state: BorrowerIndexStateModel) {
    return state.borrowerIndexItems;
  }

  @Selector()
  static borrowerIndexPage(state: BorrowerIndexStateModel) {
    return state.page;
  }

  @Selector()
  static nextPageExists(state: BorrowerIndexStateModel) {
    return state.nextPageExists;
  }

  @Selector()
  static filterParameters(state: BorrowerIndexStateModel) {
    return state.filterParameters;
  }

  @Selector()
  static noDataFound(state: BorrowerIndexStateModel) {
    return state.noDataFound;
  }

  @Selector()
  static loading(state: BorrowerIndexStateModel) {
    return state.loading;
  }

  @Selector()
  static nextPageLoading(state: BorrowerIndexStateModel) {
    return state.nextPageLoading;
  }

  @Selector()
  static allFilteredBorrowerIdsLoading(state: BorrowerIndexStateModel) {
    return state.allFilteredBorrowerIdsLoading;
  }

  constructor(
    private borrowerIndexService: BorrowerIndexService,
    private store: Store
  ) { }

  @Action(BorrowerIndexGet)
  borrowerIndexGet({ getState, setState }: StateContext<BorrowerIndexStateModel>, { }: BorrowerIndexGet) {
    const { sort, page, criteria, relationCriteria, nextPageExists } = getState();
    if ( !nextPageExists && page !== 1 ) {
      setState(
        patch({
          loading: false,
          nextPageLoading: false
        })
      );
      return;
    }

    return this.borrowerIndexService.get({
      sort,
      page,
      criteria,
      relationCriteria
    })
      .pipe(
        catchError( error => {
          console.error(error);
          return of();
        }),
      )
      .subscribe({
      next: response => {
        setState(
          patch({
            borrowerIndexItems: append(response.data),
            noDataFound: response.data.length === 0 ? true : false,
            loading: false,
            nextPageLoading: false,
            nextPageExists: response.next_page_url ? true : false
          })
        );
      }
    });
  }

  @Action(ClearBorrowerIndexStore)
  ClearBorrowerIndexStore({ patchState }: StateContext<BorrowerIndexStateModel>, { }: ClearBorrowerIndexStore) {
    patchState({
      borrowerIndexItems: [],
      loading: false,
      nextPageLoading: false,
      allFilteredBorrowerIdsLoading: false,
      sort: { 'borrowers.lastActivityId': 'DESC' },
      criteria: { 'borrowers.status': { 'notIn': ['dead', 'doNotContact'] } },
      relationCriteria: {},
      noDataFound: false,
      filterParameters: [],
    });
  }

  @Action(BorrowerIndexApplyFilter)
  BorrowerIndexApplyFilter({ patchState }: StateContext<BorrowerIndexStateModel>,
      { relationCriteria, criteria, filterParameters }: BorrowerIndexApplyFilter) {
    patchState({ relationCriteria, criteria, borrowerIndexItems: [], page: 1, nextPageExists: false, filterParameters });
    patchState({ loading: true });
    this.store.dispatch(new BorrowerIndexGet());
  }

  @Action(BorrowerIndexApplySort)
  borrowerIndexApplySort({ patchState }: StateContext<BorrowerIndexStateModel>,
      { attribute, direction }: BorrowerIndexApplySort) {
    const sort = { [attribute]: direction.toUpperCase() } as  SortStatus;
    patchState({ sort, borrowerIndexItems: [] });
    patchState({ loading: true });
    this.store.dispatch(new BorrowerIndexGet());
  }

  @Action(BorrowerIndexUpdateLoadingState)
  BorrowerIndexUpdateLoadingState({ patchState }: StateContext<BorrowerIndexStateModel>,
      { loading }: BorrowerIndexUpdateLoadingState) {
    patchState({ loading });
  }


  @Action(BorrowerIndexNextPage)
  borrowerIndexNextPage({ getState, patchState }: StateContext<BorrowerIndexStateModel>, obj: BorrowerIndexNextPage) {
    const { page } = getState();
    patchState({
      page: page + 1,
      nextPageLoading: true
    });
    this.store.dispatch(new BorrowerIndexGet());
  }
}
